/**
 * Cifra autenticada: XChaCha20-Poly1305-IETF.
 *
 * Nonce de 24 bytes tirado de `randombytes_buf` a cada chamada. Com 24 bytes
 * o risco de colisao e desprezivel mesmo sem contador — que e exatamente o
 * motivo de usar a variante X em vez da ChaCha20 de 12 bytes.
 */
import { concat } from './bytes.js'
import { DecryptionFailedError } from './errors.js'
import { SIZES, sodium } from './sodium.js'

/** Saida: nonce (24) ‖ ciphertext ‖ tag (16). */
export function seal(
  key: Uint8Array,
  plaintext: Uint8Array,
  associatedData: Uint8Array | null = null,
): Uint8Array {
  const s = sodium()
  const nonce = s.randombytes_buf(SIZES.aeadNonce)
  const ct = s.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    associatedData,
    null,
    nonce,
    key,
  )
  return concat(nonce, ct)
}

/**
 * Devolve `undefined` em vez de lancar quando `quiet` — o ratchet precisa
 * TENTAR decifrar cabecalhos com varias chaves, e falha esperada nao e erro.
 */
export function open(
  key: Uint8Array,
  sealed: Uint8Array,
  associatedData: Uint8Array | null = null,
): Uint8Array | undefined {
  if (sealed.length < SIZES.aeadNonce + SIZES.aeadTag) return undefined
  const nonce = sealed.subarray(0, SIZES.aeadNonce)
  const ct = sealed.subarray(SIZES.aeadNonce)
  try {
    return sodium().crypto_aead_xchacha20poly1305_ietf_decrypt(null, ct, associatedData, nonce, key)
  } catch {
    return undefined
  }
}

/** Igual a `open`, mas lanca. Use quando a falha realmente e um erro. */
export function openOrThrow(
  key: Uint8Array,
  sealed: Uint8Array,
  associatedData: Uint8Array | null = null,
): Uint8Array {
  const out = open(key, sealed, associatedData)
  if (out === undefined) throw new DecryptionFailedError()
  return out
}
